import { useState } from 'react'
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { createFileRoute, Link, useParams } from '@tanstack/react-router'
import type {
  EmployeeDTO,
  PolicySummaryDTO,
  TrainingProgramDTO,
} from '@deepguide-ai/types'
import { Badge } from '@deepguide-ai/ui/badge'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@deepguide-ai/ui/breadcrumb'
import { Button } from '@deepguide-ai/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { MultiSelect } from '@deepguide-ai/ui/multi-select'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@deepguide-ai/ui/table'
import { Loader2, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { trpc } from '@/lib/trpc'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import PageLoading from '@/components/PageLoading'
import { MemberAvatar } from '@/components/compliance/MemberAvatar'
import {
  getNextAssignmentGroupIds,
  getPersonGroupKind,
} from '@/components/compliance/people/group-assignment'
import MainHeader from '@/components/layout/MainHeader'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/people_groups/$groupId'
)({
  component: GroupDetailPage,
  staticData: {
    pageHeader: { title: 'Groups', customHeader: true, pad: false },
  },
})

type AddTarget = 'employee' | 'training' | 'policy'

const TABLE_HEAD_CLASS =
  'px-3 font-body text-[10px] font-semibold uppercase tracking-[1px] text-text-subtle'

function GroupDetailPage() {
  const { workspaceId, groupId } = useParams({ strict: false }) as {
    workspaceId: string
    groupId: string
  }
  const queryClient = useQueryClient()
  const { isAdmin } = useWorkspacePermissions(workspaceId)
  const [addTarget, setAddTarget] = useState<AddTarget | null>(null)
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const groupQuery = useQuery({
    ...trpc.personGroup.get.queryOptions({ id: groupId }),
    staleTime: 0,
  })
  const membersQuery = useQuery({
    ...trpc.personGroup.listMembers.queryOptions({ groupId }),
    staleTime: 0,
  })
  const employeesQuery = useQuery(
    trpc.employee.list.queryOptions({ status: 'ACTIVE', limit: 100 })
  )
  const programsQuery = useQuery(
    trpc.training.listPrograms.queryOptions({ limit: 100, offset: 0 })
  )
  const policiesQuery = useQuery(trpc.policy.list.queryOptions({ limit: 100 }))

  const employees: EmployeeDTO[] = employeesQuery.data?.items ?? []
  const programs: TrainingProgramDTO[] = programsQuery.data?.items ?? []
  const policies: PolicySummaryDTO[] = (policiesQuery.data?.items ?? []).filter(
    (policy) => policy.currentVersion > 0
  )
  const members = membersQuery.data ?? []

  const trainingAssignmentQueries = useQueries({
    queries: programs.map((program) =>
      trpc.personGroup.listTrainingAssignments.queryOptions({
        trainingProgramId: program.id,
      })
    ),
  })
  const policyAssignmentsQuery = useQuery({
    ...trpc.personGroup.listPolicyAssignments.queryOptions({}),
    queryKey: [...trpc.personGroup.listPolicyAssignments.queryKey({}), { workspaceId }],
    enabled: policies.length > 0,
  })

  const assignedPrograms = programs.filter((program, index) =>
    (trainingAssignmentQueries[index]?.data ?? []).some(
      (assignment) => assignment.groupId === groupId
    )
  )
  const assignedPolicies = policies.filter((policy) =>
    (policyAssignmentsQuery.data ?? []).some(
      (assignment) => assignment.policyDocumentId === policy.policyDocumentId && assignment.groupId === groupId
    )
  )

  const acknowledgmentsQuery = useQuery({
    ...trpc.employee.listAcknowledgments.queryOptions({
      employeeIds: members.map((member) => member.employeeId),
      policyDocumentIds: assignedPolicies.map(
        (policy) => policy.policyDocumentId
      ),
    }),
    enabled: members.length > 0 && assignedPolicies.length > 0,
  })

  const currentPolicyVersions = new Map(
    assignedPolicies.map((policy) => [
      policy.policyDocumentId,
      policy.currentVersion,
    ])
  )
  const currentAcknowledgments = new Set(
    (acknowledgmentsQuery.data ?? [])
      .filter(
        (acknowledgment) =>
          currentPolicyVersions.get(acknowledgment.templateId) ===
          acknowledgment.version
      )
      .map(
        (acknowledgment) =>
          `${acknowledgment.employeeId}|${acknowledgment.templateId}`
      )
  )

  const memberMutation = useMutation(
    trpc.personGroup.setMemberOverride.mutationOptions()
  )
  const trainingAssignmentMutation = useMutation(
    trpc.personGroup.replaceTrainingAssignments.mutationOptions()
  )
  const policyAssignmentMutation = useMutation(
    trpc.personGroup.replacePolicyAssignments.mutationOptions()
  )

  const isMutating =
    memberMutation.isPending ||
    trainingAssignmentMutation.isPending ||
    policyAssignmentMutation.isPending

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: trpc.personGroup.get.queryKey({ id: groupId }),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.personGroup.list.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.personGroup.listMembers.queryKey({ groupId }),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.personGroup.listTrainingAssignments.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.personGroup.listPolicyAssignments.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.training.listRecords.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.policy.list.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.employee.listAcknowledgments.queryKey(),
      }),
    ])
  }

  const getTrainingAssignments = (programId: string) => {
    const index = programs.findIndex((program) => program.id === programId)
    return trainingAssignmentQueries[index]?.data ?? []
  }

  const getPolicyAssignments = (policyDocumentId: string) =>
    (policyAssignmentsQuery.data ?? []).filter(
      (assignment) => assignment.policyDocumentId === policyDocumentId
    )

  const handleAdd = async () => {
    if (!addTarget || selectedIds.length === 0) return

    try {
      if (addTarget === 'employee') {
        for (const employeeId of selectedIds) {
          await memberMutation.mutateAsync({
            groupId,
            employeeId,
            mode: 'include',
          })
        }
      } else if (addTarget === 'training') {
        for (const programId of selectedIds) {
          await trainingAssignmentMutation.mutateAsync({
            trainingProgramId: programId,
            groupIds: getNextAssignmentGroupIds(
              getTrainingAssignments(programId),
              groupId,
              true
            ),
          })
        }
      } else {
        for (const policyDocumentId of selectedIds) {
          await policyAssignmentMutation.mutateAsync({
            policyDocumentId,
            groupIds: getNextAssignmentGroupIds(
              getPolicyAssignments(policyDocumentId),
              groupId,
              true
            ),
          })
        }
      }

      await invalidate()
      toast.success('Group updated')
      setSelectedIds([])
      setAddTarget(null)
    } catch (error) {
      await invalidate()
      toast.error(
        error instanceof Error ? error.message : 'Failed to update group'
      )
    }
  }

  const removeEmployee = async (employeeId: string) => {
    try {
      await memberMutation.mutateAsync({ groupId, employeeId, mode: 'clear' })
      await invalidate()
      toast.success('Employee removed from group')
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to remove employee'
      )
    }
  }

  const removeTraining = async (programId: string) => {
    try {
      await trainingAssignmentMutation.mutateAsync({
        trainingProgramId: programId,
        groupIds: getNextAssignmentGroupIds(
          getTrainingAssignments(programId),
          groupId,
          false
        ),
      })
      await invalidate()
      toast.success('Training removed from group')
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to remove training'
      )
    }
  }

  const removePolicy = async (policyDocumentId: string) => {
    try {
      await policyAssignmentMutation.mutateAsync({
        policyDocumentId,
        groupIds: getNextAssignmentGroupIds(
          getPolicyAssignments(policyDocumentId),
          groupId,
          false
        ),
      })
      await invalidate()
      toast.success('Policy removed from group')
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to remove policy'
      )
    }
  }

  const assignmentsLoading =
    trainingAssignmentQueries.some((query) => query.isLoading) ||
    policyAssignmentsQuery.isLoading
  const hasLoadError =
    groupQuery.isError ||
    membersQuery.isError ||
    employeesQuery.isError ||
    programsQuery.isError ||
    policiesQuery.isError ||
    trainingAssignmentQueries.some((query) => query.isError) ||
    policyAssignmentsQuery.isError ||
    acknowledgmentsQuery.isError

  if (
    groupQuery.isLoading ||
    membersQuery.isLoading ||
    employeesQuery.isLoading ||
    programsQuery.isLoading ||
    policiesQuery.isLoading ||
    assignmentsLoading ||
    acknowledgmentsQuery.isLoading
  ) {
    return <PageLoading />
  }

  const group = groupQuery.data
  if (!group || hasLoadError) {
    return (
      <div className='flex h-[50vh] flex-col items-center justify-center gap-3'>
        <p className='text-muted-foreground'>Unable to load this group.</p>
        <Button variant='outline' onClick={() => window.location.reload()}>
          Try again
        </Button>
      </div>
    )
  }

  const groupKind = getPersonGroupKind(group)
  const isDefault = groupKind === 'default'
  const isSystem = groupKind !== 'custom'
  const groupName = isDefault ? 'Default group' : group.name
  const memberIds = new Set(members.map((member) => member.employeeId))
  const assignedProgramIds = new Set(
    assignedPrograms.map((program) => program.id)
  )
  const assignedPolicyIds = new Set(
    assignedPolicies.map((policy) => policy.policyDocumentId)
  )

  const addOptions =
    addTarget === 'employee'
      ? employees
          .filter((employee) => !memberIds.has(employee.id))
          .map((employee) => ({
            value: employee.id,
            label:
              `${employee.firstName} ${employee.lastName}`.trim() ||
              employee.email,
          }))
      : addTarget === 'training'
        ? programs
            .filter((program) => !assignedProgramIds.has(program.id))
            .map((program) => ({ value: program.id, label: program.name }))
        : policies
            .filter((policy) => !assignedPolicyIds.has(policy.policyDocumentId))
            .map((policy) => ({
              value: policy.policyDocumentId,
              label: policy.title,
            }))

  return (
    <div className='bg-card flex h-full min-h-0 w-full flex-col overflow-hidden'>
      <MainHeader className='!mb-0 !h-[50px] p-0 pl-4'>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link
                  to='/workspaces/$workspaceId/people'
                  params={{ workspaceId }}
                  search={{ tab: 'groups' }}
                >
                  Groups
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{groupName}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </MainHeader>

      <ScrollArea className='min-h-0 flex-1' viewClassName='[&>div]:!block'>
        <div className='mx-auto w-full max-w-5xl space-y-8 px-6 py-8'>
          <header>
            <div className='flex items-center gap-2'>
              <h1 className='font-heading text-text-secondary text-2xl font-bold'>
                {groupName}
              </h1>
              {isSystem && (
                <Badge variant='outline'>
                  {isDefault ? 'Default' : 'System'}
                </Badge>
              )}
            </div>
            <p className='text-text-subtle mt-2 max-w-2xl text-sm'>
              {group.description ||
                (isDefault
                  ? 'All active employees are included automatically.'
                  : 'No description provided.')}
            </p>
          </header>

          <section className='space-y-3'>
            <div className='flex items-center justify-between gap-3'>
              <div>
                <h2 className='text-text-secondary text-sm font-semibold'>
                  Employees ({members.length})
                </h2>
              </div>
              {isAdmin && !isSystem && (
                <Button
                  size='sm'
                  onClick={() => {
                    setSelectedIds([])
                    setAddTarget('employee')
                  }}
                >
                  <Plus className='h-4 w-4' /> Add employee
                </Button>
              )}
            </div>
            <div className='border-border overflow-hidden rounded-sm border bg-card'>
              <Table>
                <TableHeader>
                  <TableRow className='bg-surface-subtle hover:bg-surface-subtle'>
                    <TableHead className={TABLE_HEAD_CLASS}>Employee</TableHead>
                    <TableHead className={TABLE_HEAD_CLASS}>Email</TableHead>
                    <TableHead className={TABLE_HEAD_CLASS}>
                      <span className='sr-only'>Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className='h-24 text-center'>
                        <span className='text-text-subtle text-sm'>
                          No employees in this group.
                        </span>
                      </TableCell>
                    </TableRow>
                  ) : (
                    members.map((member) => {
                      const acknowledgedCount = assignedPolicies.filter(
                        (policy) =>
                          currentAcknowledgments.has(
                            `${member.employeeId}|${policy.policyDocumentId}`
                          )
                      ).length
                      return (
                        <TableRow key={member.employeeId}>
                          <TableCell className='px-3 py-3'>
                            <div className='flex items-center gap-2.5'>
                              <MemberAvatar person={member} size='sm' />
                              <span className='text-text-secondary text-sm font-medium'>
                                {`${member.firstName} ${member.lastName}`.trim() ||
                                  member.email}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className='text-text-subtle px-3 py-3 text-sm'>
                            {member.email}
                          </TableCell>
                          <TableCell className='px-3 py-3 text-right'>
                            {isAdmin && !isSystem && (
                              <Button
                                size='xs'
                                variant='ghost'
                                className='hover:text-destructive hover:bg-destructive/10'
                                disabled={isMutating}
                                onClick={() =>
                                  void removeEmployee(member.employeeId)
                                }
                              >
                                <Trash2 className='h-3.5 w-3.5' />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </section>

          <section className='space-y-3'>
            <div className='flex items-center justify-between gap-3'>
              <div>
                <h2 className='text-text-secondary text-sm font-semibold'>
                  Training ({assignedPrograms.length})
                </h2>
              </div>
              {isAdmin && (
                <Button
                  size='sm'
                  onClick={() => {
                    setSelectedIds([])
                    setAddTarget('training')
                  }}
                >
                  <Plus className='h-4 w-4' /> Add training
                </Button>
              )}
            </div>
            <div className='border-border overflow-hidden rounded-sm border bg-card'>
              <Table>
                <TableHeader>
                  <TableRow className='bg-surface-subtle hover:bg-surface-subtle'>
                    <TableHead className={TABLE_HEAD_CLASS}>Program</TableHead>
                    <TableHead className={TABLE_HEAD_CLASS}>Type</TableHead>
                    <TableHead className={TABLE_HEAD_CLASS}>Due</TableHead>
                    <TableHead className={TABLE_HEAD_CLASS}>
                      <span className='sr-only'>Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {assignedPrograms.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className='h-24 text-center'>
                        <span className='text-text-subtle text-sm'>
                          No training assigned to this group.
                        </span>
                      </TableCell>
                    </TableRow>
                  ) : (
                    assignedPrograms.map((program) => (
                      <TableRow key={program.id}>
                        <TableCell className='text-text-secondary px-3 py-3 text-sm font-medium'>
                          {program.name}
                        </TableCell>
                        <TableCell className='px-3 py-3'>
                          <Badge variant='outline'>
                            {program.required ? 'Required' : 'Optional'}
                          </Badge>
                        </TableCell>
                        <TableCell className='text-text-subtle px-3 py-3 text-sm'>
                          {program.dueDateDays
                            ? `${program.dueDateDays} days`
                            : '—'}
                        </TableCell>
                        <TableCell className='px-3 py-3 text-right'>
                          {isAdmin && (
                            <Button
                              size='xs'
                              variant='ghost'
                              className='hover:text-destructive hover:bg-destructive/10'
                              disabled={isMutating}
                              onClick={() => void removeTraining(program.id)}
                            >
                              <Trash2 className='h-3.5 w-3.5' />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </section>

          <section className='space-y-3'>
            <div className='flex items-center justify-between gap-3'>
              <div>
                <h2 className='text-text-secondary text-sm font-semibold'>
                  Policies ({assignedPolicies.length})
                </h2>
              </div>
              {isAdmin && (
                <Button
                  size='sm'
                  onClick={() => {
                    setSelectedIds([])
                    setAddTarget('policy')
                  }}
                >
                  <Plus className='h-4 w-4' /> Add policy
                </Button>
              )}
            </div>
            <div className='border-border overflow-hidden rounded-sm border bg-card'>
              <Table>
                <TableHeader>
                  <TableRow className='bg-surface-subtle hover:bg-surface-subtle'>
                    <TableHead className={TABLE_HEAD_CLASS}>Policy</TableHead>
                    <TableHead className={TABLE_HEAD_CLASS}>Version</TableHead>
                    <TableHead className={TABLE_HEAD_CLASS}>
                      <span className='sr-only'>Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {assignedPolicies.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className='h-24 text-center'>
                        <span className='text-text-subtle text-sm'>
                          No policies assigned to this group.
                        </span>
                      </TableCell>
                    </TableRow>
                  ) : (
                    assignedPolicies.map((policy) => {
                      const acknowledgedCount = members.filter((member) =>
                        currentAcknowledgments.has(
                          `${member.employeeId}|${policy.policyDocumentId}`
                        )
                      ).length
                      return (
                        <TableRow key={policy.policyDocumentId}>
                          <TableCell className='text-text-secondary px-3 py-3 text-sm font-medium'>
                            {policy.title}
                          </TableCell>
                          <TableCell className='text-text-subtle px-3 py-3 text-sm'>
                            v{policy.currentVersion}
                          </TableCell>
                          <TableCell className='px-3 py-3 text-right'>
                            {isAdmin && (
                              <Button
                                size='xs'
                                variant='ghost'
                                className='hover:text-destructive hover:bg-destructive/10'
                                disabled={isMutating}
                                onClick={() =>
                                  void removePolicy(policy.policyDocumentId)
                                }
                              >
                                <Trash2 className='h-3.5 w-3.5' />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </section>
        </div>
      </ScrollArea>

      <Dialog
        open={addTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedIds([])
            setAddTarget(null)
          }
        }}
      >
        <DialogContent className='sm:max-w-[560px]'>
          <DialogHeader>
            <DialogTitle>
              Add {addTarget === 'employee' ? 'employees' : addTarget}
            </DialogTitle>
            <DialogDescription>
              Select one or more items to add to {groupName}.
            </DialogDescription>
          </DialogHeader>
          <div className='px-6'>
            {addOptions.length === 0 ? (
              <div className='text-text-subtle flex min-h-28 items-center justify-center text-sm'>
                Nothing else is available to add.
              </div>
            ) : (
              <MultiSelect
                options={addOptions}
                value={selectedIds}
                onValueChange={setSelectedIds}
                placeholder={`Select ${addTarget ?? 'items'}`}
                maxCount={3}
                modalPopover
                className='min-h-10'
              />
            )}
          </div>

          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setAddTarget(null)}
              disabled={isMutating}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void handleAdd()}
              disabled={selectedIds.length === 0 || isMutating}
            >
              {isMutating && <Loader2 className='h-4 w-4 animate-spin' />}
              Add selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
